import { useCallback, useEffect, useState } from "react";
import { PlatformSupportTicketAPI, SupportTicketAPI, SupportUnreadSummary } from "../services/supportTicket.service";

export type SupportScope = "clinic" | "admin";

const REFRESH_EVENT = "support-unread-refresh";
const POLL_MS = 30_000;
const EMPTY: SupportUnreadSummary = { total: 0, tickets: [] };

/** Ask every mounted useSupportUnread to refetch (call after reading/replying). */
export function refreshSupportUnread(): void {
  window.dispatchEvent(new Event(REFRESH_EVENT));
}

/**
 * Unread support-chat replies for the current viewer. `clinic` = replies from the
 * support team; `admin` = replies/new tickets from clinics. Polls, and refetches
 * on demand via refreshSupportUnread(). Failures are silent — it's only a badge.
 */
export function useSupportUnread(scope: SupportScope, enabled = true) {
  const [summary, setSummary] = useState<SupportUnreadSummary>(EMPTY);

  const refresh = useCallback(() => {
    if (!enabled) return;
    const api = scope === "admin" ? PlatformSupportTicketAPI : SupportTicketAPI;
    api.unreadSummary().then(setSummary).catch(() => { /* non-critical */ });
  }, [scope, enabled]);

  useEffect(() => {
    refresh();
    const id = window.setInterval(refresh, POLL_MS);
    window.addEventListener(REFRESH_EVENT, refresh);
    return () => {
      window.clearInterval(id);
      window.removeEventListener(REFRESH_EVENT, refresh);
    };
  }, [refresh]);

  const markAllRead = useCallback(async () => {
    const api = scope === "admin" ? PlatformSupportTicketAPI : SupportTicketAPI;
    setSummary(EMPTY);
    try { await api.markAllRead(); } catch { /* next poll corrects it */ }
    refreshSupportUnread();
  }, [scope]);

  return { summary, refresh, markAllRead };
}
